import { toast } from 'react-toastify';

const queue = [];
let _originalSuccess = null;
let _queryClient = null;
let _nextQueuedId = 1;

/** Maps queued toast IDs to the actual toast IDs returned by react-toastify. */
const _queuedIdMap = new Map();

/**
 * Resolve a queued toast ID to the actual react-toastify toast ID.
 * Returns undefined if the queued toast hasn't been flushed yet or was
 * never queued (i.e., it fired immediately and the returned ID is already
 * the real one).
 *
 * @param {string} queuedId - A `queued-toast-*` ID returned by the wrapped toast.success.
 * @returns {string|number|undefined} The real toast ID, or undefined if not yet flushed.
 */
export function resolveToastId(queuedId) {
  return _queuedIdMap.get(queuedId);
}

/**
 * Flush all queued success toasts using the original toast.success.
 * Maps each queued ID to the actual toast ID so callers can later
 * dismiss or update toasts via resolveToastId().
 */
function flushQueue() {
  if (queue.length === 0) return;
  const items = [...queue];
  queue.length = 0;
  items.forEach(({ id, message, options }) => {
    const actualId = _originalSuccess(message, options);
    _queuedIdMap.set(id, actualId);
  });
}

/**
 * Wire up deferred success toasts so they fire only after every
 * in-flight React Query mutation has settled.
 *
 * - Replaces `toast.success` with a version that queues when mutations
 *   are active and passes through immediately when they are not.
 * - When queued, returns a stable `queued-toast-*` ID so callers can
 *   later resolve the real toast ID via `resolveToastId()`.
 * - Subscribes to the mutation cache so the queue auto-flushes once
 *   `isMutating()` reaches 0.
 * - Uses `requestAnimationFrame` before flushing so React has time to
 *   re-render the GlobalSaveIndicator to "Saved!" first.
 * - Only `toast.success` is deferred; error / info / warning fire instantly.
 * - No-ops during SSR (no window / requestAnimationFrame available).
 *
 * @param {import('@tanstack/react-query').QueryClient} queryClient
 */
export function initToastQueue(queryClient) {
  if (typeof window === 'undefined') return;
  if (_originalSuccess) return;
  _originalSuccess = toast.success.bind(toast);
  _queryClient = queryClient;

  toast.success = (message, options) => {
    if (_queryClient.isMutating() > 0) {
      const id = `queued-toast-${_nextQueuedId++}`;
      queue.push({ id, message, options });
      return id;
    }
    return _originalSuccess(message, options);
  };

  queryClient.getMutationCache().subscribe(() => {
    if (queryClient.isMutating() === 0 && queue.length > 0) {
      requestAnimationFrame(() => {
        if (_queryClient.isMutating() === 0) {
          flushQueue();
        }
      });
    }
  });
}
